/**
 * Dashboard Page
 * Analytics dashboard with charts and metrics
 */

import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useTimeStore } from '../store/timeStore';
import { useClientStore } from '../store/clientStore';
import { useInvoiceStore } from '../store/invoiceStore';
import { useDataSubscriptions } from '../hooks/useDataSubscriptions';
import { formatCurrency, formatHours, isOverdue } from '../utils/formatters';
import './Dashboard.css';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const Dashboard = () => {
  useDataSubscriptions();

  const timeEntries = useTimeStore((state) => state.timeEntries);
  const clients = useClientStore((state) => state.clients);
  const invoices = useInvoiceStore((state) => state.invoices);

  // Calculate basic stats
  const unbilledEntries = timeEntries.filter((e) => !e.invoiced && !e.isRunning);
  const totalUnbilledHours = unbilledEntries.reduce((sum, e) => sum + (e.duration || 0), 0) / 3600;
  const totalUnbilledAmount = unbilledEntries.reduce((sum, e) => sum + (e.amount || 0), 0);

  const pendingInvoices = invoices.filter((i) => i.status === 'pending' || i.status === 'sent');
  const overdueInvoices = pendingInvoices.filter((i) => isOverdue(i.dueDate));
  const totalPending = pendingInvoices.reduce((sum, i) => sum + (i.totalAmount || 0), 0);

  const paidInvoices = invoices.filter((i) => i.status === 'paid');
  const totalRevenue = paidInvoices.reduce((sum, i) => sum + (i.totalAmount || 0), 0);

  // Calculate monthly revenue data (last 6 months)
  const monthlyRevenue = useMemo(() => {
    const months = [];
    const now = new Date();

    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear();
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      const monthInvoices = paidInvoices.filter((inv) => {
        const invDate = inv.paidAt?.toDate ? inv.paidAt.toDate() : new Date(inv.paidAt || inv.issueDate);
        return invDate >= monthStart && invDate <= monthEnd;
      });

      const revenue = monthInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

      months.push({
        name: `${monthKey}`,
        revenue: revenue,
        invoices: monthInvoices.length,
      });
    }

    return months;
  }, [paidInvoices]);

  // Invoice status distribution
  const invoiceStatusData = useMemo(() => {
    const statusCounts = {
      paid: 0,
      pending: 0,
      sent: 0,
      overdue: 0,
      draft: 0,
    };

    invoices.forEach((inv) => {
      if (inv.status === 'paid') {
        statusCounts.paid++;
      } else if (isOverdue(inv.dueDate) && inv.status !== 'paid') {
        statusCounts.overdue++;
      } else if (inv.status === 'sent') {
        statusCounts.sent++;
      } else if (inv.status === 'pending') {
        statusCounts.pending++;
      } else {
        statusCounts.draft++;
      }
    });

    return Object.entries(statusCounts)
      .filter(([, value]) => value > 0)
      .map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value,
      }));
  }, [invoices]);

  // Revenue by client (top 5)
  const revenueByClient = useMemo(() => {
    const clientRevenue = {};

    paidInvoices.forEach((inv) => {
      const clientName = inv.clientName || 'Unknown';
      clientRevenue[clientName] = (clientRevenue[clientName] || 0) + (inv.totalAmount || 0);
    });

    return Object.entries(clientRevenue)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, revenue]) => ({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        revenue,
      }));
  }, [paidInvoices]);

  // Recent payments (last 5 paid invoices)
  const recentPayments = useMemo(() => {
    return [...paidInvoices]
      .sort((a, b) => {
        const dateA = a.paidAt?.toDate ? a.paidAt.toDate() : new Date(a.paidAt || a.issueDate);
        const dateB = b.paidAt?.toDate ? b.paidAt.toDate() : new Date(b.paidAt || b.issueDate);
        return dateB - dateA;
      })
      .slice(0, 5);
  }, [paidInvoices]);

  // Custom tooltip for currency
  const CurrencyTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.name === 'revenue' ? formatCurrency(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p className="page-subtitle">Welcome back! Here's your FusionICT billing overview.</p>
        </div>
        <div className="header-actions">
          <Link to="/invoices" className="btn btn-primary">
            + Create Invoice
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#dbeafe' }}>
            💰
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Revenue</div>
            <div className="stat-value">{formatCurrency(totalRevenue)}</div>
            <div className="stat-meta">{paidInvoices.length} paid invoices</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fef3c7' }}>
            ⏰
          </div>
          <div className="stat-content">
            <div className="stat-label">Unbilled Time</div>
            <div className="stat-value">{formatHours(totalUnbilledHours * 3600)} hrs</div>
            <div className="stat-meta">{formatCurrency(totalUnbilledAmount)} value</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#e0e7ff' }}>
            📄
          </div>
          <div className="stat-content">
            <div className="stat-label">Pending Invoices</div>
            <div className="stat-value">{formatCurrency(totalPending)}</div>
            <div className="stat-meta">{pendingInvoices.length} outstanding</div>
          </div>
        </div>

        <div className="stat-card">
          <div
            className="stat-icon"
            style={{ backgroundColor: overdueInvoices.length > 0 ? '#fee2e2' : '#d1fae5' }}
          >
            {overdueInvoices.length > 0 ? '⚠️' : '✓'}
          </div>
          <div className="stat-content">
            <div className="stat-label">Overdue Invoices</div>
            <div className="stat-value">{overdueInvoices.length}</div>
            <div className="stat-meta">
              {overdueInvoices.length > 0 ? 'Needs attention' : 'All up to date'}
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        {/* Revenue Trend Chart */}
        <div className="chart-card chart-wide">
          <h3>Revenue Trend (Last 6 Months)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyRevenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" />
              <YAxis stroke="#6b7280" tickFormatter={(value) => `₱${(value / 1000).toFixed(0)}k`} />
              <Tooltip content={<CurrencyTooltip />} />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#6366f1"
                strokeWidth={3}
                dot={{ fill: '#6366f1', strokeWidth: 2 }}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Invoice Status Pie Chart */}
        <div className="chart-card">
          <h3>Invoice Status</h3>
          {invoiceStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={invoiceStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {invoiceStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-chart">
              <p>No invoices yet</p>
            </div>
          )}
        </div>

        {/* Revenue by Client Bar Chart */}
        <div className="chart-card">
          <h3>Top Clients by Revenue</h3>
          {revenueByClient.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={revenueByClient} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" stroke="#6b7280" tickFormatter={(value) => `₱${(value / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" stroke="#6b7280" width={100} />
                <Tooltip content={<CurrencyTooltip />} />
                <Bar dataKey="revenue" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-chart">
              <p>No revenue data yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Section */}
      <div className="dashboard-sections">
        {/* Quick Actions */}
        <div className="dashboard-section">
          <h2>Quick Actions</h2>
          <div className="action-cards">
            <Link to="/time" className="action-card">
              <span className="action-icon">⏱️</span>
              <span className="action-label">Track Time</span>
            </Link>
            <Link to="/clients" className="action-card">
              <span className="action-icon">👥</span>
              <span className="action-label">Manage Clients</span>
            </Link>
            <Link to="/invoices" className="action-card">
              <span className="action-icon">📋</span>
              <span className="action-label">View Invoices</span>
            </Link>
            <Link to="/subscriptions" className="action-card">
              <span className="action-icon">🔄</span>
              <span className="action-label">Subscriptions</span>
            </Link>
          </div>
        </div>

        {/* Recent Payments */}
        <div className="dashboard-section">
          <h2>Recent Payments</h2>
          <div className="card">
            {recentPayments.length === 0 ? (
              <p className="empty-state-text">No payments recorded yet.</p>
            ) : (
              <div className="payments-list">
                {recentPayments.map((invoice) => (
                  <div key={invoice.id} className="payment-item">
                    <div className="payment-info">
                      <span className="payment-client">{invoice.clientName || 'Client'}</span>
                      <span className="payment-date">
                        {invoice.paidAt?.toDate
                          ? invoice.paidAt.toDate().toLocaleDateString()
                          : new Date(invoice.paidAt || invoice.issueDate).toLocaleDateString()}
                      </span>
                    </div>
                    <span className="payment-amount">{formatCurrency(invoice.totalAmount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="dashboard-section">
          <h2>Summary</h2>
          <div className="card">
            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-value">{clients.length}</span>
                <span className="summary-label">Total Clients</span>
              </div>
              <div className="summary-item">
                <span className="summary-value">{invoices.length}</span>
                <span className="summary-label">Total Invoices</span>
              </div>
              <div className="summary-item">
                <span className="summary-value">{timeEntries.filter((e) => !e.isRunning).length}</span>
                <span className="summary-label">Time Entries</span>
              </div>
              <div className="summary-item">
                <span className="summary-value">{paidInvoices.length}</span>
                <span className="summary-label">Paid Invoices</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
